import { useEffect } from 'react';

/**
 * Hook to handle clicks outside a component
 * @param ref - React ref to the element that should be excluded from outside clicks
 * @param onOutsideClick - Callback function to execute when a click outside occurs
 * @param enabled - Whether the hook should be active
 *
 * NOTE: This is maintained for backward compatibility.
 * New components should use useOutsideClickHandler directly.
 */
export const useOutsideClick = (
  ref: React.RefObject<HTMLElement> | React.RefObject<HTMLElement>[],
  onOutsideClick: (event?: MouseEvent | TouchEvent) => void,
  enabled = true
) => {
  // Always use useEffect to ensure hooks rules are followed
  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // Check if click was outside based on whether ref is an array or single ref
      let clickedOutside: boolean;

      if (Array.isArray(ref)) {
        // Check if click was outside all refs in the array
        clickedOutside = ref.every(r => {
          return !r.current || !r.current.contains(event.target as Node);
        });
      } else {
        // Single ref case
        clickedOutside = !ref.current || !ref.current.contains(event.target as Node);
      }

      if (clickedOutside) {
        onOutsideClick(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, onOutsideClick, enabled]);
};
